import type { TopRadio } from '@/mocks/superAdminAnalytics';

interface TopRankingListProps {
  title: string;
  data: TopRadio[];
  valueLabel: string;
  valueSuffix?: string;
  onViewRadio?: (radio: TopRadio) => void;
  accentColor?: string;
}

function formatValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function getAccentBar(idx: number, accentColor?: string): string {
  if (accentColor) return accentColor;
  const colors = ['bg-amber-500', 'bg-amber-400', 'bg-amber-300', 'bg-amber-200', 'bg-amber-100'];
  return colors[idx % colors.length];
}

export default function TopRankingList({ title, data, valueLabel, valueSuffix = '', onViewRadio, accentColor }: TopRankingListProps) {
  if (data.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <h4 className="text-foreground-800 text-xs font-semibold mb-3">{title}</h4>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center mb-2">
            <i className="ri-search-line text-foreground-300 text-lg"></i>
          </div>
          <p className="text-foreground-400 text-xs">Sin datos con estos filtros</p>
        </div>
      </div>
    );
  }

  const maxValue = data[0]?.value ?? 1;

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-foreground-800 text-xs font-semibold">{title}</h4>
        <span className="text-foreground-300 text-[9px]">{valueLabel}</span>
      </div>
      <div className="space-y-2">
        {data.map((item, idx) => {
          const pct = (item.value / maxValue) * 100;
          const isPositive = item.changePercent >= 0;

          return (
            <div key={item.rank} className="group" style={{ animation: `fadeInUp 0.2s ease-out ${idx * 0.04}s both` }}>
              <div className="flex items-center gap-2.5">
                {/* Rank */}
                <span className={`w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
                  item.rank === 1 ? 'bg-amber-500 text-background-50' :
                  item.rank === 2 ? 'bg-foreground-300 text-background-50' :
                  item.rank === 3 ? 'bg-amber-200 text-amber-800' :
                  'bg-background-100 text-foreground-400'
                }`}>
                  {item.rank}
                </span>

                {/* Radio info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-md ${item.iconBg} flex items-center justify-center shrink-0`}>
                      <i className="ri-radio-line text-foreground-500 text-[9px]"></i>
                    </div>
                    <div className="min-w-0">
                      <button
                        onClick={() => onViewRadio?.(item)}
                        className="text-foreground-800 text-[11px] font-semibold truncate hover:text-accent-500 transition-colors cursor-pointer text-left"
                      >
                        {item.radioName}
                      </button>
                      <p className="text-foreground-300 text-[9px]">{item.frequency}</p>
                    </div>
                  </div>

                  {/* Bar */}
                  <div className="mt-1.5 w-full h-1 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getAccentBar(idx, accentColor)} transition-all group-hover:brightness-110`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Value */}
                <div className="text-right shrink-0">
                  <p className="text-foreground-800 text-[11px] font-mono font-bold">{formatValue(item.value)}{valueSuffix}</p>
                  <p className={`text-[9px] font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isPositive ? '+' : ''}{item.changePercent.toFixed(1)}%
                  </p>
                </div>

                {/* View detail button */}
                {onViewRadio && (
                  <button
                    onClick={() => onViewRadio(item)}
                    className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-accent-100"
                    title="Ver detalle de radio"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}